import { DefaultLayout } from '@/shared/ui/DefaultLayout'
import { AlertIcon } from '@/shared/ui/icons/AlertIcon'
import { SubmitResultScreen } from '@/shared/ui/SubmitResultScreen'
import { RegisteredCompaniesRow } from '@/public/features/org/ui/RegisteredCompaniesRow'
import { CheckBadge } from '@/public/features/org/ui/orgNew/Done'
import { makeLabeler, useCommonCodes } from '@/shared/lib/codes'
import type { OrgNewDraft } from '@/public/features/org/types'
import { CARD_MAX } from '@/public/features/org/constants/orgNew'

/** 사이드바 카드에 태그를 몇 개까지 늘어놓고 나머지를 「+N」으로 접는지 — 시안 1291:23056. */
const TAG_LIMIT = 2

/**
 * PC 해결카드 등록 완료(시안 1291:22955).
 * 왼쪽은 모바일과 **같은 완료 화면 부품**(`SubmitResultScreen`)을 쓰고, 오른쪽에 등록한
 * 해결카드 목록을 둔다. 아래 「등록된 기업」은 기업 해결카드 목록 화면이 쓰는
 * `RegisteredCompaniesRow` 를 그대로 쓴다(사용자 지시 2026-09-07).
 */
export function OrgNewPcCardDone({ draft }: { draft: OrgNewDraft }) {
  return (
    <DefaultLayout tree="pc" className="[--body-w:1000px]">
      <div className="layout-body flex flex-col gap-[60px] pt-8 pb-[60px]">
        <div className="flex w-full items-start gap-[44px]">
          {/*
            `SubmitResultScreen` 은 모바일 폭(343)을 기준으로 가운데 몰리게 되어 있다.
            PC 시안은 **640 칸을 꽉 쓰고 일러스트만 가운데**다 — 공용 부품을 고치지 않고
            이 화면에서만 그 제한을 푼다.
          */}
          <div className="flex w-[640px] shrink-0 flex-col gap-[60px] [&>section]:mx-0 [&>section]:max-w-none [&_img]:mx-auto [&_img]:object-center">
            <SubmitResultScreen
              icon={<CheckBadge />}
              title="해결 카드 등록을 완료했어요."
              titleWeight="semibold"
              description="등록하신 기업 정보와 해결카드는 검토 및 승인 후 서비스에 공개됩니다."
              gap={60}
            />
            <p className="m-0 flex items-center justify-center gap-2 rounded-xl bg-[#f8f9fc] px-2.5 py-3.5 text-body-sm font-medium text-black-300 [&>svg]:h-[22px] [&>svg]:w-[22px] [&>svg]:shrink-0">
              <AlertIcon />
              <span>
                현재 페이지를 나가면 해결카드를 추가로 등록할 수 없으니 해결카드를 모두 등록한 후
                완료해 주세요.
              </span>
            </p>
          </div>

          <CardSidebar draft={draft} />
        </div>

        <RegisteredCompaniesRow paddingTop="pt-0" />
      </div>
    </DefaultLayout>
  )
}

/** 오른쪽 사이드바 — 등록한 해결카드와 남은 자리(시안 1291:23043). */
function CardSidebar({ draft }: { draft: OrgNewDraft }) {
  // **서버에서 다시 읽지 않는다** — 갓 등록한 기업·카드는 승인 전이라 `GET /api/orgs/:id` 가
  // 404 를 낸다(`apps/api/src/routes/org.ts` 의 `is_published` · `review_status` 조건).
  // 방금 만든 카드는 draft 가 들고 있다.
  const cards = draft.createdCards
  // 남은 자리는 상한에서 등록한 수를 뺀다 — 상한은 3장이다(대외 공표값).
  const emptySlots = Math.max(0, CARD_MAX - cards.length)

  return (
    <aside className="flex w-[316px] shrink-0 flex-col gap-[18px] overflow-hidden rounded-[18px] bg-[#f8f9fc] px-[26px] py-7">
      <div className="flex w-full items-center gap-2">
        <h2 className="m-0 text-body-lg font-semibold text-black-600">등록한 해결카드</h2>
        <span className="rounded-[11px] border border-[rgb(0_0_0/7%)] bg-[#fffbf2] px-3 py-1 text-body-sm font-medium text-[#5c4808]">
          {`총 ${cards.length}장`}
        </span>
      </div>

      <div className="flex w-full flex-col gap-5">
        {cards.map((c, i) => (
          <SidebarCard key={i} card={c} />
        ))}
        {Array.from({ length: emptySlots }, (_, i) => (
          <button
            key={i}
            type="button"
            className="ui-pressable flex h-[140px] w-full cursor-pointer flex-col items-center justify-center gap-2.5 rounded-2xl border border-white-200 bg-white-50 px-6 py-[22px] shadow-[0_0_4px_-1px_rgb(0_0_0/8%)] disabled:cursor-not-allowed"
            disabled={!draft.canMakeMoreCards}
            onClick={draft.startNextCard}
          >
            <span className="flex items-center text-black-100 [&>svg]:h-8 [&>svg]:w-8" aria-hidden>
              <PlusIcon />
            </span>
            <span className="truncate text-body-md font-medium text-black-200">
              해결카드 추가하기
            </span>
          </button>
        ))}
      </div>
    </aside>
  )
}

function SidebarCard({ card }: { card: { title: string; categoryCodes: string[] } }) {
  const categories = useCommonCodes('category')
  const categoryLabel = makeLabeler(categories.data)

  const shown = card.categoryCodes.slice(0, TAG_LIMIT)
  const hidden = card.categoryCodes.length - shown.length

  return (
    <div className="flex h-[140px] w-full flex-col gap-[11px] overflow-hidden rounded-2xl border border-white-200 bg-white-50 px-6 py-[22px] shadow-[0_0_4px_-1px_rgb(0_0_0/8%)]">
      <div className="flex w-full flex-col gap-3.5">
        <div className="flex items-center gap-2">
          {shown.map((code) => (
            <span
              key={code}
              className="rounded-lg bg-[#ebebeb] px-3 py-1 text-body-sm font-medium whitespace-nowrap text-black-400"
            >
              {categoryLabel(code)}
            </span>
          ))}
          {hidden > 0 && (
            <span className="rounded-lg border border-[rgb(0_0_0/4%)] bg-white-100 px-3 py-1 text-body-sm font-medium text-white-900">
              {`+${hidden}`}
            </span>
          )}
        </div>
        <p className="m-0 w-full truncate text-body-lg-reading font-semibold text-black-800">
          {card.title}
        </p>
      </div>
    </div>
  )
}

/** 시안의 32px 더하기 아이콘(1291:23658) — 프로젝트에 같은 글리프가 없어 여기 둔다. */
function PlusIcon() {
  return (
    <svg viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
      <path
        d="M16 6.667v18.666M6.667 16h18.666"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
      />
    </svg>
  )
}
